'use client';

import { BrandMark } from './BrandMark';

export type BrandWordmarkSize = 'sm' | 'md' | 'lg' | 'xl';
export type BrandWordmarkTone = 'default' | 'onDark';
export type BrandWordmarkSuffix = 'Documents' | 'Admin';

export type BrandWordmarkProps = {
  size?: BrandWordmarkSize;
  showIcon?: boolean;
  className?: string;
  tone?: BrandWordmarkTone;
  suffix?: BrandWordmarkSuffix;
  priority?: boolean;
};

const SIZE_MAP: Record<
  BrandWordmarkSize,
  { icon: number; text: string }
> = {
  sm: { icon: 24, text: 'text-sm' },
  md: { icon: 32, text: 'text-lg' },
  lg: { icon: 48, text: 'text-xl' },
  xl: { icon: 64, text: 'text-2xl' },
};

/** Official wordmark: icon + Trace (dark) + Documents (brand purple). */
export function BrandWordmark({
  size = 'md',
  showIcon = true,
  className = '',
  tone = 'default',
  suffix = 'Documents',
  priority = false,
}: BrandWordmarkProps) {
  const { icon, text } = SIZE_MAP[size];

  const traceColor =
    tone === 'onDark'
      ? 'var(--brand-wordmark-trace-on-dark, var(--sidebar-text-bright, #f8fafc))'
      : 'var(--brand-wordmark-trace, var(--foreground, #0f172a))';

  const suffixColor =
    suffix === 'Admin'
      ? tone === 'onDark'
        ? 'var(--brand-wordmark-admin-on-dark, #f87171)'
        : 'var(--brand-wordmark-admin, #dc2626)'
      : tone === 'onDark'
        ? 'var(--brand-wordmark-accent-on-dark, #a5b4fc)'
        : 'var(--brand-wordmark-accent, var(--primary, #4f46e5))';

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      {showIcon ? <BrandMark size={icon} priority={priority} className="shrink-0" /> : null}
      <span className={`font-bold tracking-tight ${text}`} aria-label="TraceDocuments">
        <span style={{ color: traceColor }}>Trace</span>
        <span style={{ color: suffixColor }}>{suffix}</span>
      </span>
    </span>
  );
}
